import mockJson from './mock';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { ReloadOutlined, SearchOutlined, UserOutlined } from '@ant-design/icons';
import { Avatar, Button, Drawer, Form, Input, message, Modal, Select, Space, Spin, Switch, Table, Tag } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { useSnapshot } from 'valtio';
import request from '@/api/network';
import {
  createPetBreedApi,
  deletePetBreedApi,
  getPetBreedDetailApi,
  getPetBreedListApi,
  updatePetBreedApi,
} from '@/api/petManage/breedManage';
import { getLifeStageByBodySizeApi } from '@/api/petManage/lifeStage';
import { getPetFieldDictListApi } from '@/api/petManage/petFieldDict';
import ImageUploader from '@/component/ImageUploader';
import { state } from '@/store';
import { checkPermissions } from '@/utils/utils';
import { PET_BREED_KEY } from '@/router/Permissions';

const petTypeOptions = [
  { label: '全部', value: '' },
  { label: '猫', value: '2' },
  { label: '狗', value: '1' },
];
const displayOptions = [
  { label: '全部', value: '' },
  { label: '是', value: 1 },
  { label: '否', value: 0 },
];

const BreedManage: React.FC = () => {
  const { currentShopPlatformType } = useSnapshot(state);
  const [form] = Form.useForm();
  const [drawerForm] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [dataSource, setDataSource] = useState<any[]>([]);
  const [total, setTotal] = useState(0);
  const [pageNum, setPageNum] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [drawerVisible, setDrawerVisible] = useState(false);
  const [drawerLoading, setDrawerLoading] = useState(false);
  const [drawerSaving, setDrawerSaving] = useState(false);
  const [currentBreed, setCurrentBreed] = useState<any>(null);
  const [bodySizeOptions, setBodySizeOptions] = useState<{ label: string; value: string }[]>([]);
  const [bodySizeSelectOptions, setBodySizeSelectOptions] = useState<{ label: string; value: string }[]>([]);
  const [lifeStages, setLifeStages] = useState<any[]>([]);
  const [lifeStageLoading, setLifeStageLoading] = useState(false);
  const [drawerMode, setDrawerMode] = useState<'edit' | 'create'>('edit');
  const [batchVisible, setBatchVisible] = useState(false);
  const mockTableData = useMemo(() => Object.values(mockJson || {}).flat(), []);
  const [batchCreating, setBatchCreating] = useState<Record<string | number, boolean>>({});
  const [switchLoading, setSwitchLoading] = useState<Record<string | number, boolean>>({});
  const canToggleHot = useMemo(() => checkPermissions(PET_BREED_KEY.IS_HOT), []);
  const canToggleShow = useMemo(() => checkPermissions(PET_BREED_KEY.IS_SHOW), []);
  const canEditBreed = useMemo(() => checkPermissions(PET_BREED_KEY.EDIT), []);

  const fetchList = useCallback(
    async (override?: Partial<{ pageNum: number; pageSize: number }>) => {
      const values = form.getFieldsValue();
      const params = {
        petType: values.petType || undefined,
        breedName: values.breedName || undefined,
        isShow: values.isShow !== '' ? values.isShow : undefined,
        pageNum: override?.pageNum || pageNum,
        pageSize: override?.pageSize || pageSize,
      };
      setLoading(true);
      try {
        const res = await getPetBreedListApi(params);
        if (res.code === 200) {
          setDataSource(res.rows || []);
          setTotal(res.total || 0);
          if (override?.pageNum) setPageNum(override.pageNum);
          if (override?.pageSize) setPageSize(override.pageSize);
        }
      } catch (error) {
        console.error('获取品种列表失败', error);
      } finally {
        setLoading(false);
      }
    },
    [form, pageNum, pageSize],
  );

  // 预加载体型选项，供列表渲染展示 label
  const fetchAllBodySize = async () => {
    try {
      const dogRes = await getPetFieldDictListApi({ fieldCode: 'bodySize', petType: '1' });
      const options = (dogRes?.data || [])
        .filter(Boolean)
        .map((item: any) => ({ label: item.dictName, value: String(item.dictCode) }));
      setBodySizeOptions(options);
      setBodySizeSelectOptions(options);
    } catch (error) {
      console.error('预加载体型字典失败', error);
    }
  };

  useEffect(() => {
    fetchAllBodySize().finally(() => {
      fetchList({ pageNum: 1 });
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchLifeStages = useCallback(async (petType: string, bodySize: string) => {
    if (!petType || !bodySize) {
      setLifeStages([]);
      return;
    }
    try {
      setLifeStageLoading(true);
      const res = await getLifeStageByBodySizeApi({ petType, bodySize });
      if (res.code === 200) {
        setLifeStages(res.data || []);
      }
    } catch (error) {
      console.error('获取生命阶段失败', error);
    } finally {
      setLifeStageLoading(false);
    }
  }, []);

  const uploadImageByUrl = useCallback(async (url: string) => {
    const res = await fetch(url);
    if (!res.ok) throw new Error('图片下载失败');
    const blob = await res.blob();
    const file = new File([blob], 'upload.png', { type: blob.type || 'image/png' });
    const formData = new FormData();
    formData.append('file', file);
    if (state.currentShopId) formData.append('shopId', String(state.currentShopId));
    if (state.currentShopPlatformType) formData.append('platform', String(state.currentShopPlatformType));
    const uploadRes: any = await request({
      url: '/member/royalcanin/applet/image/uploadImage',
      method: 'POST',
      data: formData,
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    if (uploadRes?.code !== 200) throw new Error(uploadRes?.msg || '上传失败');
    const jdImageUrl = uploadRes?.data?.jdImageUrl;
    const tmImageUrl = uploadRes?.data?.tmImageUrl;
    const imageUrl = uploadRes?.data?.imageUrl || tmImageUrl || jdImageUrl;
    if (!imageUrl && !jdImageUrl && !tmImageUrl) throw new Error('未返回图片链接');
    return imageUrl || tmImageUrl || jdImageUrl || '';
  }, []);

  const handleAutoCreate = useCallback(
    async (record: any) => {
      const key = record.id || record.breedCode;
      setBatchCreating(prev => ({ ...prev, [key]: true }));
      try {
        let imgUrl = record.thumbnailUrl || '';
        if (record.thumbnailUrl) {
          try {
            imgUrl = await uploadImageByUrl(record.thumbnailUrl);
          } catch (err: any) {
            console.error('图片上传失败，使用空图继续', err);
            message.warning(err?.message || '图片上传失败，已跳过图片');
            imgUrl = '';
          }
        }
        const payload = {
          breedCode: record.breedCode,
          petType: record.breedTypeId ? String(record.breedTypeId) : undefined,
          breedName: record.breedName,
          keyWords: record.keywords || '',
          firstLetter: record.firstLetter || '',
          bodySize: record.bodySize,
          isShow: record.isDisplay ? 1 : 0,
          isHot: 0,
          imgUrl,
        };
        const res = await createPetBreedApi(payload);
        if (res.code === 200) {
          message.success('创建成功');
          fetchList({ pageNum: 1 });
        }
      } catch (error) {
        // 已在上层展示错误
      } finally {
        setBatchCreating(prev => ({ ...prev, [key]: false }));
      }
    },
    [fetchList, uploadImageByUrl],
  );

  const handleEdit = useCallback(
    async (record: any) => {
      setDrawerMode('edit');
      setDrawerVisible(true);
      setDrawerLoading(true);
      setLifeStages([]);
      drawerForm.resetFields();
      try {
        const res = await getPetBreedDetailApi(record.id);
        if (res.code === 200 && res.data) {
          const detail = res.data;
          setCurrentBreed(detail);
          const keywordsArray = detail.keyWords ? detail.keyWords.split(',').filter(Boolean) : [];
          setBodySizeSelectOptions(detail.petType === '1' || detail.petType === 1 ? bodySizeOptions : []);
          drawerForm.setFieldsValue({
            breedCode: detail.breedCode,
            petType: detail.petType,
            breedName: detail.breedName,
            keyWords: keywordsArray,
            firstLetter: detail.firstLetter || '',
            bodySize: detail.bodySize != null ? String(detail.bodySize) : undefined,
            isShow: detail.isShow === 1,
            isHot: detail.isHot === 1,
            imgUrl: {
              jdUrl: detail.jdImgUrl,
              tmUrl: detail.tmImgUrl,
              imageUrl: detail.imgUrl,
            },
          });
          if (detail.bodySize) fetchLifeStages(detail.petType, detail.bodySize);
        } else {
          message.error(res.msg || '获取品种详情失败');
        }
      } catch (error) {
        console.error('获取品种详情失败', error);
        message.error('获取品种详情失败');
      } finally {
        setDrawerLoading(false);
      }
    },
    [drawerForm, fetchLifeStages, bodySizeOptions],
  );

  const handleBodySizeChange = (value: string) => {
    if (currentBreed?.petType) {
      fetchLifeStages(currentBreed.petType, value);
    } else {
      const petType = drawerForm.getFieldValue('petType');
      if (petType) fetchLifeStages(petType, value);
    }
  };

  const handleFirstLetterChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const upper = e.target.value.toUpperCase();
    drawerForm.setFieldsValue({ firstLetter: upper });
  };

  const handleCreate = () => {
    setDrawerMode('create');
    setCurrentBreed(null);
    setDrawerVisible(true);
    setDrawerLoading(false);
    setLifeStages([]);
    setBodySizeSelectOptions([]);
    drawerForm.resetFields();
    drawerForm.setFieldsValue({ isShow: true, isHot: false });
  };

  const handleSubmit = async () => {
    try {
      const values = await drawerForm.validateFields();
      const imgVal = values.imgUrl;
      const hasImg = typeof imgVal === 'string' ? !!imgVal : !!(imgVal?.imageUrl || imgVal?.jdUrl || imgVal?.tmUrl);
      if (!hasImg) {
        message.error('请上传缩略图');
        return;
      }
      setDrawerSaving(true);
      const jdImgUrl = imgVal?.jdUrl || (typeof imgVal === 'string' ? imgVal : undefined);
      const tmImgUrl = imgVal?.tmUrl;
      let res;
      if (drawerMode === 'edit' && currentBreed) {
        const payload = {
          ...currentBreed,
          breedName: values.breedName,
          keyWords: (values.keyWords || []).join(','),
          firstLetter: values.firstLetter,
          bodySize: values.bodySize,
          isShow: values.isShow ? 1 : 0,
          isHot: values.isHot ? 1 : 0,
          jdImgUrl,
          tmImgUrl,
          imgUrl: undefined,
        };
        res = await updatePetBreedApi(payload);
      } else {
        const payload = {
          breedCode: values.breedCode,
          petType: values.petType,
          breedName: values.breedName,
          keyWords: (values.keyWords || []).join(','),
          firstLetter: values.firstLetter,
          bodySize: values.bodySize,
          isShow: values.isShow ? 1 : 0,
          isHot: values.isHot ? 1 : 0,
          jdImgUrl,
          tmImgUrl,
          imgUrl: undefined,
        };
        res = await createPetBreedApi(payload);
      }
      if (res.code === 200) {
        message.success(drawerMode === 'edit' ? '保存成功' : '创建成功');
        setDrawerVisible(false);
        setCurrentBreed(null);
        drawerForm.resetFields();
        fetchList({ pageNum: 1 });
      }
    } catch (error: any) {
      if (error?.errorFields) return;
      console.error('保存失败', error);
    } finally {
      setDrawerSaving(false);
    }
  };

  const handleDelete = useCallback(
    (record: any) => {
      Modal.confirm({
        title: '确认删除该品种？',
        content: `品种名称：${record.breedName}`,
        onOk: async () => {
          try {
            const res = await deletePetBreedApi(record.id);
            if (res.code === 200) {
              message.success('删除成功');
              fetchList({ pageNum: 1 });
            }
          } catch (error) {
            console.error('删除品种失败', error);
          }
        },
      });
    },
    [fetchList],
  );

  const handleToggleField = useCallback(
    async (record: any, field: 'isShow' | 'isHot', checked: boolean) => {
      const id = record.id;
      setSwitchLoading(prev => ({ ...prev, [id]: true }));
      try {
        const payload = {
          ...record,
          [field]: checked ? 1 : 0,
        };
        const res = await updatePetBreedApi(payload);
        if (res.code === 200) {
          message.success('修改成功');
          setDataSource(prev => prev.map(item => (item.id === id ? { ...item, [field]: checked ? 1 : 0 } : item)));
          fetchList();
        }
      } catch (error) {
        console.error('更新失败', error);
      } finally {
        setSwitchLoading(prev => ({ ...prev, [id]: false }));
      }
    },
    [fetchList],
  );

  const columns: ColumnsType = useMemo(
    () => [
      { title: '品种名称', dataIndex: 'breedName', width: 160 },
      {
        title: '分类',
        dataIndex: 'petType',
        width: 100,
        render: value => {
          if (value === '1' || value === 1) return '狗';
          if (value === '2' || value === 2) return '猫';
          return value || '——';
        },
      },
      {
        title: '体型',
        dataIndex: 'bodySize',
        width: 120,
        render: (value, record) => {
          if (record.petType === '2' || record.petType === 2) return <Tag color="default">——</Tag>;
          const match = bodySizeOptions.find(opt => opt.value === String(value));
          const label = match?.label || value || '——';
          return <Tag color="gold">{label}</Tag>;
        },
      },
      { title: '关键词', dataIndex: 'keyWords', width: 200, ellipsis: true },
      {
        title: '缩略图',
        dataIndex: 'imgUrl',
        width: 120,
        render: (_, record: any) => {
          let src = record.imgUrl;
          if (currentShopPlatformType === 'TM') {
            src = record.tmImgUrl || record.imgUrl || record.jdImgUrl;
          } else if (currentShopPlatformType === 'JD') {
            src = record.jdImgUrl || record.imgUrl || record.tmImgUrl;
          } else {
            src = record.tmImgUrl || record.jdImgUrl || record.imgUrl;
          }
          return (
            <Avatar
              shape="square"
              size={48}
              src={src}
              icon={<UserOutlined />}
            />
          );
        },
      },
      {
        title: '热门品种',
        dataIndex: 'isHot',
        width: 140,
        render: (value, record: any) => (
          <Switch
            checked={value === 1}
            loading={switchLoading[record.id]}
            disabled={!canToggleHot}
            onChange={checked => handleToggleField(record, 'isHot', checked)}
          />
        ),
      },
      {
        title: '是否显示',
        dataIndex: 'isShow',
        width: 140,
        render: (value, record: any) => (
          <Switch
            checked={value === 1}
            loading={switchLoading[record.id]}
            disabled={!canToggleShow}
            onChange={checked => handleToggleField(record, 'isShow', checked)}
          />
        ),
      },
      { title: '创建时间', dataIndex: 'createTime', width: 180 },
      {
        title: '操作',
        width: 100,
        fixed: 'right',
        render: (_, record) => (
          <Space>
            {canEditBreed && (
              <Button
                type="link"
                onClick={() => handleEdit(record)}>
                编辑
              </Button>
            )}
            <Button
              hidden
              type="link"
              danger
              onClick={() => handleDelete(record)}>
              删除
            </Button>
          </Space>
        ),
      },
    ],
    [
      handleEdit,
      handleDelete,
      bodySizeOptions,
      currentShopPlatformType,
      handleToggleField,
      switchLoading,
      canToggleHot,
      canToggleShow,
      canEditBreed,
    ],
  );
  const batchColumns = useMemo(
    () => [
      { title: '品种名称', dataIndex: 'breedName', width: 180 },
      { title: 'BreedCode', dataIndex: 'breedCode', width: 180 },
      {
        title: '缩略图',
        dataIndex: 'thumbnailUrl',
        width: 120,
        render: (value: string) => (
          <Avatar
            shape="square"
            size={48}
            src={value}
            icon={<UserOutlined />}
          />
        ),
      },
      { title: '首字母', dataIndex: 'firstLetter', width: 100 },
      { title: '体型', dataIndex: 'bodySize', width: 100 },
      {
        title: '是否显示',
        dataIndex: 'isDisplay',
        width: 140,
        render: (value: boolean | number) => (
          <Switch
            checked={!!value}
            disabled
          />
        ),
      },
      {
        title: '操作',
        dataIndex: 'action',
        width: 160,
        render: (_: any, record: any) => {
          const loading = batchCreating[record.id] || false;
          return (
            <Button
              type="link"
              onClick={() => handleAutoCreate(record)}
              loading={loading}>
              自动新建
            </Button>
          );
        },
      },
    ],
    [batchCreating, handleAutoCreate],
  );

  return (
    <div style={{ padding: 24 }}>
      <Form
        form={form}
        layout="inline"
        style={{ marginBottom: 16, rowGap: 12 }}
        preserve={false}>
        <Form.Item
          label="宠物分类"
          name="petType"
          initialValue="">
          <Select
            placeholder="全部"
            style={{ width: 160 }}
            options={petTypeOptions}
          />
        </Form.Item>
        <Form.Item
          label="品种名称"
          name="breedName">
          <Input
            placeholder="请输入"
            style={{ width: 220 }}
            allowClear
          />
        </Form.Item>
        <Form.Item
          label="是否显示"
          name="isShow"
          initialValue="">
          <Select
            placeholder="全部"
            style={{ width: 140 }}
            options={displayOptions}
          />
        </Form.Item>
        <Form.Item>
          <Space>
            <Button
              type="primary"
              icon={<SearchOutlined />}
              onClick={() => fetchList({ pageNum: 1 })}>
              查询
            </Button>
            <Button
              icon={<ReloadOutlined />}
              onClick={() => {
                form.resetFields();
                setPageNum(1);
                fetchList({ pageNum: 1 });
              }}>
              重置
            </Button>
            <Button
              hidden
              type="primary"
              onClick={handleCreate}>
              新建
            </Button>
            <Button
              hidden
              type="primary"
              ghost
              onClick={() => setBatchVisible(true)}>
              批量新建
            </Button>
          </Space>
        </Form.Item>
      </Form>

      <Table
        rowKey="id"
        loading={loading}
        columns={columns}
        dataSource={dataSource}
        pagination={{
          current: pageNum,
          pageSize,
          total,
          showSizeChanger: true,
          showQuickJumper: true,
          showTotal: t => `共 ${t} 条`,
          onChange: (page, size) => fetchList({ pageNum: page, pageSize: size }),
        }}
        scroll={{ x: 1000 }}
      />
      <Modal
        width={900}
        title="批量新建"
        open={batchVisible}
        onCancel={() => setBatchVisible(false)}
        footer={null}>
        <Table
          rowKey="id"
          size="small"
          columns={batchColumns}
          dataSource={mockTableData}
          pagination={{ pageSize: 10 }}
          scroll={{ x: 800, y: 400 }}
        />
      </Modal>
      <Drawer
        width={600}
        destroyOnHidden
        open={drawerVisible}
        title={drawerMode === 'edit' ? '编辑品种信息' : '新建品种信息'}
        onClose={() => {
          setDrawerVisible(false);
          setCurrentBreed(null);
          drawerForm.resetFields();
          setLifeStages([]);
          setBodySizeSelectOptions([]);
          setDrawerMode('edit');
        }}
        extra={
          <Space>
            <Button onClick={() => setDrawerVisible(false)}>取消</Button>
            <Button
              type="primary"
              loading={drawerSaving}
              onClick={handleSubmit}>
              保存
            </Button>
          </Space>
        }>
        <Spin spinning={drawerLoading}>
          <Form
            form={drawerForm}
            layout="horizontal"
            labelCol={{ flex: '0 0 100px' }}
            wrapperCol={{ flex: '1 0 0' }}
            labelAlign="right">
            {drawerMode === 'edit' ? (
              <>
                <Form.Item label="BreedCode">
                  <Input
                    disabled
                    value={currentBreed?.breedCode}
                  />
                </Form.Item>
                <Form.Item label="宠物分类">
                  <Input
                    disabled
                    value={currentBreed?.petType === '1' ? '狗' : '猫'}
                  />
                </Form.Item>
              </>
            ) : (
              <>
                <Form.Item
                  label="BreedCode"
                  name="breedCode"
                  rules={[{ required: true, message: '请输入BreedCode' }]}>
                  <Input placeholder="请输入BreedCode" />
                </Form.Item>
                <Form.Item
                  label="宠物分类"
                  name="petType"
                  rules={[{ required: true, message: '请选择宠物分类' }]}>
                  <Select
                    placeholder="请选择"
                    options={petTypeOptions.filter(item => item.value)}
                    onChange={value => {
                      setBodySizeSelectOptions(value === '1' || value === 1 ? bodySizeOptions : []);
                      drawerForm.setFieldsValue({ bodySize: undefined });
                      setLifeStages([]);
                    }}
                  />
                </Form.Item>
              </>
            )}
            <Form.Item
              label="品种名称"
              name="breedName"
              rules={[{ required: true, message: '请输入品种名称' }]}>
              <Input placeholder="请输入" />
            </Form.Item>
            <Form.Item
              label="关键词"
              name="keyWords">
              <Select
                mode="tags"
                placeholder="请输入并按回车"
              />
            </Form.Item>
            <Form.Item
              label="首字母 A-Z"
              name="firstLetter"
              rules={[{ required: true, message: '请输入首字母' }]}>
              <Input
                placeholder="请输入"
                onChange={handleFirstLetterChange}
                maxLength={1}
              />
            </Form.Item>
            {(() => {
              const petType = drawerMode === 'edit' ? currentBreed?.petType : drawerForm.getFieldValue('petType');
              const isCat = petType === '2' || petType === 2;
              if (isCat) return null;
              return (
                <Form.Item
                  label="体型"
                  name="bodySize"
                  rules={[{ required: true, message: '请选择体型' }]}>
                  <Select
                    placeholder="请选择体型"
                    options={bodySizeSelectOptions}
                    onChange={handleBodySizeChange}
                  />
                </Form.Item>
              );
            })()}
            <Form.Item
              label="是否显示"
              name="isShow"
              valuePropName="checked">
              <Switch />
            </Form.Item>
            <Form.Item
              label="热门品种"
              name="isHot"
              valuePropName="checked">
              <Switch />
            </Form.Item>
            <Form.Item
              label="缩略图"
              name="imgUrl"
              rules={[{ required: true, message: '请上传缩略图' }]}>
              <ImageUploader
                crop
                sync={1}
                limitHeight={200}
                limitWidth={200}
                outputWidth={200}
                outputHeight={200}
              />
            </Form.Item>
            <Form.Item
              label="生命阶段"
              colon={false}
              style={{ marginTop: 24 }}>
              <Table
                rowKey={record => record.id || record.stageName}
                size="small"
                pagination={false}
                loading={lifeStageLoading}
                dataSource={lifeStages}
                columns={[
                  { title: '阶段名称', dataIndex: 'stageName', width: 120 },
                  {
                    title: '月龄范围',
                    dataIndex: 'minMonths',
                    width: 160,
                    render: (_, record: any) => {
                      const min = record.minMonths ?? 0;
                      const max = record.maxMonths ?? '以上';
                      return `${min} - ${max}${typeof max === 'number' ? '月龄' : ''}`;
                    },
                  },
                  { title: '描述', dataIndex: 'content' },
                ]}
              />
            </Form.Item>
          </Form>
        </Spin>
      </Drawer>
    </div>
  );
};

export default BreedManage;
